import { LucideIcon } from 'lucide-react';
import { cn } from 'src/utils/cn';

export type RuleTab = 'general' | 'alerts' | 'actions';

export interface RuleSettingsTab {
  id: RuleTab;
  label: string;
  icon: LucideIcon;
  badge?: number;
}

interface RuleSettingsTabsProps {
  tabs: RuleSettingsTab[];
  activeTab: RuleTab;
  onChange: (tab: RuleTab) => void;
}

/**
 * Shared segmented tab bar for the rule settings panel. Used by both the
 * loan-level rule builder (RuleData) and the admin checklist-management rule
 * builder (RuleManagement) so the two stay visually identical.
 */
export function RuleSettingsTabs({
  tabs,
  activeTab,
  onChange,
}: Readonly<RuleSettingsTabsProps>) {
  return (
    <div
      role="tablist"
      aria-label="Rule settings sections"
      className="flex shrink-0 gap-1 rounded-lg bg-gray-100 p-1"
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`rule-tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`rule-panel-${tab.id}`}
            onClick={() => onChange(tab.id)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              isActive
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{tab.label}</span>
            {tab.badge ? (
              <span
                className={cn(
                  'ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold',
                  isActive
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-200 text-gray-600',
                )}
              >
                {tab.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default RuleSettingsTabs;
